import { TFunction } from 'i18next';
import AdminTenant from 'models/adminTenant';

export enum SubscriptionTypes {
  Basic = 0,
  Business = 1,
  Premium = 2,
  NIS2 = 3,
  Container = 19,
  Portal = 20,
  Internal = 100,
}

//The lifecycle state of a customer, as one exclusive value. The numbers are the filter values the status
//checkboxes in FilterTenants post, so a filter selection is a list of these.
export enum TenantStatus {
  ActiveCustomer = 0,
  InTrial = 1,
  TrialExpired = -1,
  SubscriptionEnded = 8,
  Suspended = 9,
  //not offered as a filter option: only a container org unit, which carries no subscription of its own
  None = 99,
}

/**
 * Classify a customer into exactly one lifecycle status.
 *
 * Deliberately does NOT look at stripeCustomerId. Only the top level tenant of a holding ever carries one
 * (see IntegrationSubscriptionManager.GetStripeCustomerAsync), and a customer billed through its partner has
 * none at all, so requiring it classified every organizational unit and every partner-billed customer as "not
 * an active customer". What remains is exactly what the Paid licenses tile counts server side
 * (Tenant.IsLoginAllowed plus no trial), so the tile equals the sum of the Licenses column over the rows this
 * returns ActiveCustomer for.
 */
export function getTenantStatus(tenant: AdminTenant): TenantStatus {
  if (tenant.suspended) {
    return TenantStatus.Suspended;
  }

  if (tenant.subscriptionType === SubscriptionTypes.Container) {
    return TenantStatus.None;
  }

  if (tenant.isInTrial()) {
    return tenant.trialEnded() ? TenantStatus.TrialExpired : TenantStatus.InTrial;
  }

  //the server grants a few days of grace after this date before it blocks the login; a customer inside that
  //window still counts as paid there and reads as ended here
  if (tenant.subscriptionEnd !== undefined && tenant.subscriptionEnd <= new Date()) {
    return TenantStatus.SubscriptionEnded;
  }

  return TenantStatus.ActiveCustomer;
}

export function getTenantStatusText(status: TenantStatus, t: TFunction<string[]>): string {
  switch (status) {
    case TenantStatus.ActiveCustomer:
      return t('adminSubscription:Status.ActiveCustomer');
    case TenantStatus.InTrial:
      return t('adminSubscription:Status.InTrial');
    case TenantStatus.TrialExpired:
      return t('adminSubscription:Status.TrialExpired');
    case TenantStatus.SubscriptionEnded:
      return t('adminSubscription:Status.SubscriptionEnded');
    case TenantStatus.Suspended:
      return t('adminSubscription:Status.Suspended');
    default:
      return t('adminSubscription:Status.None');
  }
}
